import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Box, Group, NumberInput, Paper, Stack, Text } from '@mantine/core'
import { useElementSize } from '@mantine/hooks'
import type { SeatingRoomConfig, SeatingTable } from '@/lib/types'
import {
  ROOM_LIMITS,
  clampPosition,
  getTableGuestNames,
  type SeatingGroup,
} from '@/lib/seatingUtils'
import styles from './SeatingChart.module.css'

interface RoomPlanEditorProps {
  tables: SeatingTable[]
  room: SeatingRoomConfig
  allGroups: SeatingGroup[]
  assignments: Record<string, string | null>
  onMoveTable: (tableId: string, x: number, y: number) => void
  onRoomChange: (patch: Partial<SeatingRoomConfig>) => void
}

interface DragState {
  tableId: string
  /** Offset (room units) between the pointer and the table center at grab time. */
  offsetX: number
  offsetY: number
}

export function RoomPlanEditor({
  tables,
  room,
  allGroups,
  assignments,
  onMoveTable,
  onRoomChange,
}: RoomPlanEditorProps) {
  const { ref: sizeRef, width: measuredWidth } = useElementSize()
  const canvasRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<DragState | null>(null)
  const [draggingTableId, setDraggingTableId] = useState<string | null>(null)

  // Scale the hall to fit the available width; never upscale beyond 1px per unit.
  const availableWidth = measuredWidth > 0 ? measuredWidth : room.roomWidth
  const scale = Math.min(availableWidth / room.roomWidth, 1)
  const canvasWidth = room.roomWidth * scale
  const canvasHeight = room.roomHeight * scale
  const tableRadius = room.tableDiameter / 2

  const pointerToRoom = useCallback(
    (clientX: number, clientY: number) => {
      const rect = canvasRef.current?.getBoundingClientRect()
      if (!rect) return { x: 0, y: 0 }
      return {
        x: (clientX - rect.left) / scale,
        y: (clientY - rect.top) / scale,
      }
    },
    [scale]
  )

  const moveDraggedTable = useCallback(
    (clientX: number, clientY: number) => {
      const dragState = dragStateRef.current
      if (!dragState) return

      const point = pointerToRoom(clientX, clientY)
      const x = clampPosition(point.x - dragState.offsetX, tableRadius, room.roomWidth)
      const y = clampPosition(point.y - dragState.offsetY, tableRadius, room.roomHeight)
      onMoveTable(dragState.tableId, x, y)
    },
    [onMoveTable, pointerToRoom, room.roomHeight, room.roomWidth, tableRadius]
  )

  const handleTablePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, table: SeatingTable) => {
      if (table.position_x == null || table.position_y == null) return

      event.preventDefault()
      event.stopPropagation()
      canvasRef.current?.setPointerCapture(event.pointerId)

      const point = pointerToRoom(event.clientX, event.clientY)
      dragStateRef.current = {
        tableId: table.id,
        offsetX: point.x - table.position_x,
        offsetY: point.y - table.position_y,
      }
      setDraggingTableId(table.id)
    },
    [pointerToRoom]
  )

  const handleCanvasPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragStateRef.current) return
      event.preventDefault()
      moveDraggedTable(event.clientX, event.clientY)
    },
    [moveDraggedTable]
  )

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) return

    if (canvasRef.current?.hasPointerCapture(event.pointerId)) {
      canvasRef.current.releasePointerCapture(event.pointerId)
    }
    dragStateRef.current = null
    setDraggingTableId(null)
  }, [])

  return (
    <Stack gap="sm">
      <Group gap="md" wrap="wrap" align="flex-end">
        <NumberInput
          label="Saal-Breite (cm)"
          value={room.roomWidth}
          onChange={(value) => onRoomChange({ roomWidth: toNumber(value, room.roomWidth) })}
          min={ROOM_LIMITS.minRoomWidth}
          max={ROOM_LIMITS.maxRoomWidth}
          step={50}
          clampBehavior="strict"
          size="sm"
          style={{ width: 150 }}
        />
        <NumberInput
          label="Saal-Höhe (cm)"
          value={room.roomHeight}
          onChange={(value) => onRoomChange({ roomHeight: toNumber(value, room.roomHeight) })}
          min={ROOM_LIMITS.minRoomHeight}
          max={ROOM_LIMITS.maxRoomHeight}
          step={50}
          clampBehavior="strict"
          size="sm"
          style={{ width: 150 }}
        />
        <NumberInput
          label="Tisch-Durchmesser (cm)"
          value={room.tableDiameter}
          onChange={(value) => onRoomChange({ tableDiameter: toNumber(value, room.tableDiameter) })}
          min={ROOM_LIMITS.minTableDiameter}
          max={ROOM_LIMITS.maxTableDiameter}
          step={10}
          clampBehavior="strict"
          size="sm"
          style={{ width: 190 }}
        />
      </Group>

      <Text size="xs" c="dimmed">
        Tische per Drag &amp; Drop im Saal positionieren. Alle Tische haben denselben Durchmesser.
      </Text>

      <Box ref={sizeRef} className={styles.roomPlanViewport}>
        {tables.length === 0 ? (
          <Paper p="md" withBorder className={styles.noTables}>
            <Text size="sm" ta="center" c="dimmed">
              Noch keine Tische vorhanden. Fügen Sie zuerst Tische hinzu.
            </Text>
          </Paper>
        ) : (
          <div
            ref={canvasRef}
            className={styles.roomCanvas}
            style={{ width: canvasWidth, height: canvasHeight }}
            onPointerMove={handleCanvasPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            {tables.map((table) => {
              const centerX = (table.position_x ?? 0) * scale
              const centerY = (table.position_y ?? 0) * scale
              const size = room.tableDiameter * scale
              const guestNames = getTableGuestNames(table.id, allGroups, assignments)
              const isDragging = draggingTableId === table.id
              const guestListLabel =
                guestNames.length > 0 ? `, Gäste: ${guestNames.join(', ')}` : ''
              return (
                <div
                  key={table.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Tisch ${table.name} verschieben${guestListLabel}`}
                  className={`${styles.roomTableCluster} ${isDragging ? styles.roomTableClusterDragging : ''}`}
                  style={{
                    left: centerX,
                    top: centerY,
                  }}
                  onPointerDown={(event) => handleTablePointerDown(event, table)}
                >
                  <div
                    className={`${styles.roomTable} ${isDragging ? styles.roomTableDragging : ''}`}
                    style={{ width: size, height: size }}
                  >
                    <span className={styles.roomTableName}>{table.name}</span>
                    {guestNames.length > 0 && (
                      <ul className={styles.roomTableGuests} aria-hidden="true">
                        {guestNames.map((name, index) => (
                          <li
                            key={`${table.id}-${index}-${name}`}
                            className={styles.roomTableGuestName}
                            title={name}
                          >
                            {name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Box>
    </Stack>
  )
}

function toNumber(value: number | string, fallback: number): number {
  if (typeof value === 'number') return value
  const parsed = parseFloat(value)
  return Number.isNaN(parsed) ? fallback : parsed
}
